// @ts-nocheck
import React, { useContext, useEffect, useRef, useState } from 'react';

import PlacesAutocomplete, {
  PropTypes as PlacesAutocompleteProps,
} from 'react-places-autocomplete';

import MapsContext from './GoogleMapsJsApiContext';

import { Rejecter, Resolver } from '@utils/promise/types';

import { log } from '@hooks/logger';

import { LogLevel } from '@typings/operations';

type callback = () => void;
interface PlacesAutocompleteElement extends PlacesAutocomplete {
  debouncedFetchPredictions: () => void;
}

declare global {
  interface Window {
    [callbackName: string]: callback;
  }
}

const globalCallbackCheckIntervalMs = 200;
const invokeEventualGlobalCallback = (
  interval: number,
  timeout: number,
  callbackName: string,
  args: unknown[]
) => {
  if (window[callbackName]) {
    if (typeof window[callbackName] === 'function') {
      window[callbackName]();
    } else {
      throw new Error(
        `Tried to invoke ${callbackName}, but it's not a function.`
      );
    }
  } else {
    const remainingTimeout = timeout - interval;
    if (remainingTimeout > 0) {
      setTimeout(
        invokeEventualGlobalCallback.bind(
          null,
          interval,
          remainingTimeout,
          callbackName,
          args
        ),
        interval
      );
    } else {
      // throw new Error(`Tried to invoke ${callbackName}, but it never appeared.`)
    }
  }
};
// @ts-ignore
const executeGetCurrentRef = function<T>(
  this: React.MutableRefObject<T>,
  patience: number,
  resolve: Resolver<T>,
  reject: Rejecter
): void {
  // eslint-disable-line func-names
  if (this.current) {
    resolve(this.current);
  } else if (patience > 0) {
    setTimeout(
      // The linter apparently can't figure out the type that results
      // when binding parameters to a function with a parameterized
      // type.
      // @ts-ignore
      executeGetCurrentRef.bind(this, patience - 1, resolve, reject),
      globalCallbackCheckIntervalMs
    );
  } else {
    reject(this);
  }
};
const promiseCurrentRef = function<T>(
  ref: React.MutableRefObject<T>
): Promise<T> {
  // eslint-disable-line func-names
  // As above, binding functions with parameterized types is confusing.
  // @ts-ignore
  return new Promise<T>(executeGetCurrentRef.bind(ref, 10));
};

const wakeupEffect = (
  mapsPromise: Promise<unknown[]>,
  stateUpdater: (newValue: boolean) => void,
  elementRef: React.MutableRefObject<PlacesAutocompleteElement>,
  callbackName: string
): (() => void) => {
  let isCancelled = false;
  const timeouts: number[] = [];

  const refPromiser: () => Promise<PlacesAutocompleteElement> = () =>
    promiseCurrentRef(elementRef);

  // chain of promises
  mapsPromise
    .then(() =>
      invokeEventualGlobalCallback(
        globalCallbackCheckIntervalMs,
        10000,
        callbackName,
        []
      )
    )
    .then(() => {
      if (!isCancelled) stateUpdater(true);
    })
    .then(() => refPromiser())
    .then(el => {
      if (!isCancelled && el) {
        el.debouncedFetchPredictions();
      }
    })
    .catch(error =>
      log({
        level: LogLevel.Error,
        message: `Couldn't get a ref for the PlacesAutocomplete element: ${error.toString()}`,
      })
    );

  // ✅ cleanup: cancel timeouts & mark as cancelled
  return () => {
    isCancelled = true;
    timeouts && timeouts.forEach(clearTimeout);
  };
};

const ContextIntegratedPlacesAutocomplete: React.FC<PlacesAutocompleteProps> = ({
  googleCallbackName,
  ...props
}) => {
  const mapsPromise = useContext<Promise<unknown[]>>(MapsContext);
  const [shouldFetchSuggestions, fetchSuggestions] = useState<boolean>(false);
  const paElement = useRef<PlacesAutocompleteElement>(null);
  useEffect(() => {
    const cleanup = wakeupEffect(
      mapsPromise,
      fetchSuggestions,
      // @ts-ignore
      paElement,
      googleCallbackName
    );
    return cleanup;
  }, [mapsPromise, googleCallbackName]);

  return (
    <PlacesAutocomplete
      {...{ ...props, googleCallbackName }}
      ref={paElement}
      debounce={0}
      shouldFetchSuggestions={shouldFetchSuggestions}
    />
  );
};

export default ContextIntegratedPlacesAutocomplete;
